/**
 * Billing chrome for Conta and Equipe.
 *
 * Desktop: the work tree's `FloatingSidebar` carrying the settings rail —
 * back to work, then the phone hub rows — so Conta is a config screen, not a
 * work tab. Phone: settings-style back header.
 */
import type { ReactNode } from 'react';
import { PORTAL_PATHS } from '@x9/billing';
import ScreenHeader from '@x9/web-work/home/components/ScreenHeader';
import ConfigSidebarNav from '@x9/web-work/home/components/ConfigSidebarNav';
import FloatingSidebar, {
  FloatingSidebarProvider,
} from '@x9/web-work/home/components/FloatingSidebar';
import { SidebarInset } from '@x9/web-work/components/ui/sidebar';
import { useIsMobile } from '../hooks/use-mobile';
import { useUserProfile } from '../hooks/useUserProfile';
import type { PortalNavKey } from '../nav';
import { signOutBilling } from '../services/phoneAuth';

export type { PortalNavKey };

type Props = {
  active: PortalNavKey;
  onNavigate: (path: string) => void;
  title: string;
  description?: string;
  titleAction?: ReactNode;
  /** Phone back target. Desktop ignores it — the sidebar is the way out. */
  backHref?: string;
  children: ReactNode;
};

export default function PortalShell({
  active,
  onNavigate,
  title,
  description,
  titleAction,
  backHref = '/settings',
  children,
}: Props) {
  const { profile } = useUserProfile(true);
  const isMobile = useIsMobile();

  const signOut = async () => {
    await signOutBilling();
    onNavigate(PORTAL_PATHS.HOME);
  };

  return (
    <FloatingSidebarProvider className="min-w-0 bg-background">
      {isMobile ? null : (
        <FloatingSidebar
          homeHref={PORTAL_PATHS.HOME}
          tone="inverted"
          account={{
            displayName: profile.displayName,
            photoURL: profile.photoURL,
            onSignOut: () => void signOut(),
          }}
        >
          <ConfigSidebarNav active={active} />
        </FloatingSidebar>
      )}

      <SidebarInset className="mx-auto flex min-h-svh min-w-0 w-full flex-col bg-background pb-16 lg:mx-0 lg:max-w-none lg:px-10 lg:pt-0">
        <div
          data-testid="page-column"
          className="flex min-w-0 flex-1 flex-col lg:mx-auto lg:w-full lg:max-w-[1000px]"
        >
        <div className="lg:hidden">
          <ScreenHeader
            title={title}
            subtitle={description}
            backHref={backHref}
            className="pb-3"
          />
        </div>

        <header className="mb-5 hidden border-b border-border pb-4 pt-8 lg:sticky lg:top-0 lg:z-10 lg:block lg:bg-background">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-2xl font-bold tracking-tight text-foreground">{title}</h1>
              {description && (
                <p className="mt-1 text-sm text-muted-foreground">{description}</p>
              )}
            </div>
            {titleAction && <div className="shrink-0 pt-0.5">{titleAction}</div>}
          </div>
        </header>

        <div className="flex flex-col gap-3 px-4 pt-3 lg:gap-5 lg:px-0 lg:pt-0">{children}</div>
        </div>
      </SidebarInset>
    </FloatingSidebarProvider>
  );
}
